import { Ripple } from 'primereact/ripple';
import { cn } from '@/helpers/utils/functionality-utils';
import React, { useEffect, useContext } from 'react';
import { CSSTransition } from 'react-transition-group';
import type { AppMenuItemProps } from '@/types';
import { MenuContext } from './context/context';
import { Link, useLocation, useSearchParams } from 'react-router';

const AppMenuitem = (props: AppMenuItemProps) => {
  const pathname = useLocation().pathname;
  const [searchParams] = useSearchParams();
  const { activeMenu, setActiveMenu } = useContext(MenuContext);
  const item = props.item;
  const key = props.parentKey
    ? props.parentKey + '-' + props.index
    : String(props.index);
  let isActiveRoute = pathname?.split('/')[2] === item!.to?.split('/')[2];

  if (pathname?.split('/')[2] === 'marketing') {
    isActiveRoute = pathname?.split('/')[3] === item!.to?.split('/')[3];
  }

  const active = activeMenu === key || activeMenu.startsWith(key + '-');
  const onRouteChange = (url: string) => {
    if (item!.to && item!.to === url) {
      setActiveMenu(key);
    }
  };

  useEffect(() => {
    onRouteChange(pathname);
  }, [pathname, searchParams]);

  const itemClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    //avoid processing disabled items
    if (item!.disabled) {
      event.preventDefault();
      return;
    }

    //execute command
    if (item!.command) {
      item!.command({ originalEvent: event, item: item });
    }

    // toggle active state
    if (item!.items) setActiveMenu(active ? (props.parentKey as string) : key);
    else setActiveMenu(key);
  };

  const subMenu = item!.items && item!.visible !== false && (
    <CSSTransition
      timeout={{ enter: 1000, exit: 450 }}
      classNames="layout-submenu"
      in={props.root ? true : active}
      key={item!.label}
    >
      <ul>
        {item!.items.map((child, i) => {
          return (
            <AppMenuitem
              item={child}
              index={i}
              className={child.badgeClass}
              parentKey={key}
              key={child.label}
            />
          );
        })}
      </ul>
    </CSSTransition>
  );

  return (
    <li
      className={cn('', {
        'layout-root-menuitem': props.root,
        'active-menuitem': active,
      })}
    >
      {props.root && item!.visible !== false && (
        <>
          {item?.to ? (
            <div>
              <Link
                to={item.to}
                className={cn(
                  'm-0 flex cursor-pointer items-center justify-between !text-[12px] font-bold uppercase',
                  pathname.toLowerCase().includes('masters')
                    ? '!text-[var(--primary-color)]'
                    : '!text-surface-800',
                )}
              >
                {item!.label}
              </Link>
            </div>
          ) : (
            <div className="layout-menuitem-root-text m-0 flex cursor-pointer items-center justify-between !text-[12px]">
              {item!.label}
            </div>
          )}
        </>
      )}
      {(!item!.to || item!.items) && item!.visible !== false ? (
        <Link
          to={item!.url || ''}
          onClick={(e) => itemClick(e)}
          className={cn(item!.class)}
          target={item!.target}
        >
          <i className={cn('layout-menuitem-icon text-[13px]', item!.icon)}></i>
          <span className="layout-menuitem-text whitespace-nowrap">
            {item!.label}
          </span>
          {item!.items && (
            <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
          )}
          <Ripple />
        </Link>
      ) : null}

      {item!.to && !item!.items && item!.visible !== false ? (
        <Link
          prefetch="intent"
          viewTransition
          to={item!.to}
          replace={item!.replaceUrl}
          target={item!.target}
          onClick={(e) => itemClick(e)}
          className={cn(item!.class, {
            'active-route': isActiveRoute,
          })}
          tabIndex={0}
        >
          {!(typeof item!.icon === 'string') ? (
            item?.icon
          ) : (
            <i
              className={cn('layout-menuitem-icon text-[13px]', item!.icon)}
            ></i>
          )}
          <span className="layout-menuitem-text whitespace-nowrap">
            {item!.label}
          </span>
          {item!.items && (
            <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
          )}
          <Ripple />
        </Link>
      ) : null}
      <div
        className={cn('overflow-clip transition-all duration-300 ease-in-out')}
      >
        {subMenu}
      </div>
    </li>
  );
};

export default AppMenuitem;
